import { useState } from 'react';
import { authApi } from '../../api/resources';
import { C } from '../../theme/palette';

/** Longueur du code reçu par e-mail ou par SMS. */
const LONGUEUR_CODE = 6;

const champ = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid ' + C.border,
  borderRadius: 10,
  padding: '12px 12px',
  fontSize: 16,
  color: C.ink,
  background: '#fff',
  textAlign: 'center',
  outline: 'none',
};

const lien = {
  display: 'block',
  margin: '14px auto 0',
  background: 'none',
  border: 'none',
  padding: 0,
  color: C.teal,
  fontSize: 13,
  fontWeight: 800,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

/**
 * Code à six chiffres demandé sur une page publique, après une invitation
 * acceptée : la session ne s'ouvre qu'au code juste, comme à chaque connexion.
 *
 * <p>Le code est déjà parti à l'adresse que la personne vient de choisir : il
 * prouve aussi que cette adresse est la bonne. La page est hors de
 * l'application — pas de session à tenir ici : le cookie posé par le serveur
 * suffit, et l'application le trouvera en s'ouvrant.
 *
 * @param {object} props propriétés
 * @param {object} props.etat état « code requis » rendu par le serveur
 * @param {(compte: object) => void} props.onConnecte appelé une fois la session ouverte
 * @param {boolean} [props.vous] vouvoie la personne (pages du magasin)
 * @returns {import('react').ReactElement} l'étape du code
 */
export function EtapeCode({ etat, onConnecte, vous = false }) {
  const [defi, setDefi] = useState(etat.defi);
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const tente = async (action) => {
    setErreur('');
    setMessage('');
    setEnvoi(true);
    try {
      await action();
    } catch (e) {
      setErreur(
        e?.message ||
          (vous
            ? 'Le code n’a pas pu être vérifié. Réessayez dans un instant.'
            : 'Le code n’a pas pu être vérifié. Réessaie dans un instant.'),
      );
    } finally {
      setEnvoi(false);
    }
  };

  const valide = (e) => {
    e.preventDefault();
    tente(async () => onConnecte(await authApi.connexionCode(defi, code)));
  };

  const renvoie = () =>
    tente(async () => {
      const reponse = await authApi.renvoieCode(defi);
      setDefi(reponse.defi);
      setCode('');
      setMessage('Un nouveau code vient d’être envoyé. Seul le dernier reçu est valable.');
    });

  return (
    <form onSubmit={valide} noValidate>
      <h1 style={{ fontSize: 18, margin: '0 0 6px' }}>
        {vous ? 'Saisissez le code reçu par e-mail' : 'Saisis le code reçu par e-mail'}
      </h1>
      <p style={{ fontSize: 14, color: C.grey, margin: '0 0 14px', lineHeight: 1.55 }}>
        {vous
          ? 'Pour confirmer votre adresse, un code à 6 chiffres vient d’être envoyé à '
          : 'Pour confirmer ton adresse, un code à 6 chiffres vient d’être envoyé à '}
        <b style={{ color: C.ink }}>{etat.emailMasque}</b>
        {'. Il est valable 2 minutes.'}
      </p>
      <label
        htmlFor="codeConfirmation"
        style={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          color: C.muted,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 6,
        }}
      >
        Code de sécurité
      </label>
      <input
        id="codeConfirmation"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="Code à 6 chiffres"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, LONGUEUR_CODE))}
        // Espacé en grand, l'indication débordait de la case : elle garde
        // l'écriture normale.
        style={{
          ...champ,
          ...(code && {
            letterSpacing: 8,
            fontSize: 22,
            fontWeight: 800,
            fontFamily: 'monospace',
          }),
        }}
      />
      {(erreur || message) && (
        <div
          role={erreur ? 'alert' : 'status'}
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: erreur ? C.red : C.green,
            marginTop: 10,
            lineHeight: 1.5,
          }}
        >
          {erreur || message}
        </div>
      )}
      <button
        type="submit"
        disabled={envoi || code.length !== LONGUEUR_CODE}
        style={{
          width: '100%',
          marginTop: 14,
          background: C.green,
          color: '#fff',
          border: 'none',
          borderRadius: 12,
          padding: '14px 16px',
          fontSize: 15,
          fontWeight: 800,
          cursor: envoi ? 'wait' : 'pointer',
          opacity: envoi || code.length !== LONGUEUR_CODE ? 0.65 : 1,
        }}
      >
        {envoi ? 'Vérification…' : 'Valider le code'}
      </button>
      <button type="button" onClick={renvoie} disabled={envoi} style={lien}>
        Renvoyer le code
      </button>
      <p style={{ fontSize: 12, color: C.muted, margin: '14px 0 0', lineHeight: 1.5 }}>
        {vous
          ? 'Rien reçu ? Regardez dans les courriers indésirables. Si l’adresse est fausse, demandez une nouvelle invitation au magasin.'
          : 'Rien reçu ? Regarde dans les courriers indésirables. Si l’adresse est fausse, demande une nouvelle invitation à ton employeur.'}
      </p>
    </form>
  );
}
